import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Calendar, 
  Clock, 
  User, 
  Phone, 
  Mail,
  MessageSquare,
  Trash2,
  CheckCircle2,
  XCircle,
  Search,
  RefreshCw,
  CalendarDays,
  MapPin,
  ExternalLink,
  Users,
  AlertCircle,
  Link2
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AppointmentsList({ actorId }) {
  const [appointments, setAppointments] = useState([]);
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingEvents, setLoadingEvents] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [activeView, setActiveView] = useState("all");
  const [calendarConnected, setCalendarConnected] = useState(false);
  const [calendarName, setCalendarName] = useState("");
  const [lastSyncTime, setLastSyncTime] = useState(null);

  useEffect(() => {
    if (actorId) {
      fetchAppointments();
      fetchCalendarEvents();
      
      // Auto-refresh every 5 minutes (300000ms)
      const syncInterval = setInterval(() => {
        console.log("Auto-syncing appointments and calendar events...");
        fetchAppointments();
        fetchCalendarEvents();
        setLastSyncTime(new Date());
      }, 300000); // 5 minutes
      
      return () => clearInterval(syncInterval);
    }
  }, [actorId]);

  const fetchAppointments = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${BACKEND_URL}/api/appointments?actor_id=${actorId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAppointments(response.data.appointments || []);
      setLastSyncTime(new Date());
    } catch (error) {
      console.error("Failed to fetch appointments:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCalendarEvents = async () => {
    setLoadingEvents(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${BACKEND_URL}/api/calendar/events/${actorId}?days=30`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setCalendarConnected(response.data.calendar_connected);
      setCalendarName(response.data.calendar_name || "");
      
      if (response.data.success) {
        setCalendarEvents(response.data.events || []);
      } else if (response.data.error && response.data.calendar_connected) {
        // Calendar is connected but there's an error (e.g., credentials expired)
        console.error("Calendar error:", response.data.error);
        if (response.data.error.includes("invalid_client") || response.data.error.includes("invalid_grant")) {
          toast.error("Google Calendar credentials expired. Please reconnect your calendar.");
        }
      }
    } catch (error) {
      console.error("Failed to fetch calendar events:", error);
    } finally {
      setLoadingEvents(false);
    }
  };

  const updateAppointmentStatus = async (appointmentId, newStatus) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.put(
        `${BACKEND_URL}/api/appointments/${appointmentId}`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (newStatus === "confirmed" && response.data.synced_to_calendar) {
        toast.success("Appointment confirmed and added to Google Calendar!");
      } else {
        toast.success(`Appointment ${newStatus}`);
      }
      
      // Refresh both appointments and calendar events
      fetchAppointments();
      fetchCalendarEvents();
    } catch (error) {
      console.error("Failed to update appointment:", error);
      toast.error("Failed to update appointment");
    }
  };

  const deleteAppointment = async (appointmentId) => {
    if (!window.confirm("Are you sure you want to delete this appointment?")) return;
    
    try {
      const token = localStorage.getItem("token");
      await axios.delete(
        `${BACKEND_URL}/api/appointments/${appointmentId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Appointment deleted");
      fetchAppointments();
    } catch (error) {
      console.error("Failed to delete appointment:", error);
      toast.error("Failed to delete appointment");
    }
  };

  // Format date in local timezone
  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric"
    });
  };

  // Format time in local timezone
  const formatTime = (dateStr) => {
    if (!dateStr) return "";
    // Check if it's an all-day event (date only, no time)
    if (dateStr.length === 10) return "All day";
    
    const date = new Date(dateStr);
    return date.toLocaleTimeString(undefined, {
      hour: "numeric",
      minute: "2-digit",
      hour12: true
    });
  };

  // Format date and time together
  const formatDateTime = (dateStr) => {
    if (!dateStr) return "";
    if (dateStr.length === 10) return formatDate(dateStr) + " (All day)";
    
    const date = new Date(dateStr);
    return date.toLocaleString(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      scheduled: "bg-blue-100 text-blue-700",
      confirmed: "bg-green-100 text-green-700",
      completed: "bg-slate-100 text-slate-700",
      cancelled: "bg-red-100 text-red-700",
      no_show: "bg-amber-100 text-amber-700"
    };
    return <Badge className={styles[status] || styles.scheduled}>{status?.replace("_", " ")}</Badge>;
  };

  const getSourceBadge = (source) => {
    const styles = {
      whatsapp: "bg-green-100 text-green-700",
      public_chat: "bg-indigo-100 text-indigo-700",
      manual: "bg-slate-100 text-slate-700",
      google_calendar: "bg-blue-100 text-blue-700"
    };
    const labels = {
      whatsapp: "WhatsApp",
      public_chat: "Public Chat",
      manual: "Manual",
      google_calendar: "Google Calendar"
    };
    return <Badge variant="outline" className={styles[source] || styles.manual}>{labels[source] || source}</Badge>;
  };

  const getAttendeeStatusColor = (status) => {
    const colors = {
      accepted: "text-green-600",
      declined: "text-red-600",
      tentative: "text-amber-600",
      needsAction: "text-slate-400"
    };
    return colors[status] || colors.needsAction;
  };

  // Combine appointments and calendar events
  const getAllItems = () => {
    // Convert app appointments to common format
    const appItems = appointments.map(apt => ({
      ...apt,
      source: apt.source || "manual",
      title: apt.reason || apt.customer_name || "Appointment"
    }));

    // Calendar events are already formatted
    const calItems = calendarEvents.map(evt => ({
      ...evt,
      source: "google_calendar"
    }));

    // Combine and sort by start time
    const allItems = [...appItems, ...calItems].sort((a, b) => {
      const dateA = new Date(a.start_time);
      const dateB = new Date(b.start_time);
      return dateA - dateB;
    });

    return allItems;
  };

  // Filter items based on view and search
  const getFilteredItems = () => {
    let items = [];
    
    if (activeView === "all") {
      items = getAllItems();
    } else if (activeView === "calendar") {
      items = calendarEvents.map(evt => ({ ...evt, source: "google_calendar" }));
    } else if (activeView === "booked") {
      items = appointments.map(apt => ({ ...apt, source: apt.source || "manual" }));
    }

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      items = items.filter(item => 
        item.title?.toLowerCase().includes(query) ||
        item.customer_name?.toLowerCase().includes(query) ||
        item.description?.toLowerCase().includes(query) ||
        item.location?.toLowerCase().includes(query)
      );
    }

    // Apply status filter (only for app appointments)
    if (statusFilter !== "all") {
      items = items.filter(item => 
        item.source === "google_calendar" || item.status === statusFilter
      );
    }

    // Group by date
    const grouped = items.reduce((groups, item) => {
      const date = formatDate(item.start_time);
      if (!groups[date]) groups[date] = [];
      groups[date].push(item);
      return groups;
    }, {});

    return grouped;
  };

  const refreshAll = () => {
    fetchAppointments();
    fetchCalendarEvents();
  };

  if (loading && loadingEvents) {
    return (
      <Card>
        <CardContent className="py-8 flex justify-center">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
        </CardContent>
      </Card>
    );
  }

  const filteredItems = getFilteredItems();
  const totalAppointments = appointments.length;
  const totalCalendarEvents = calendarEvents.length;

  return (
    <div className="space-y-6">
      {/* Calendar Connection Status */}
      {!calendarConnected && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-amber-600" />
                <div>
                  <p className="font-medium text-amber-800">Google Calendar not connected</p>
                  <p className="text-sm text-amber-600">Connect your calendar to view and sync events</p>
                </div>
              </div>
              <Button variant="outline" size="sm" className="border-amber-300 text-amber-700 hover:bg-amber-100">
                <Link2 className="w-4 h-4 mr-2" />
                Connect Calendar
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {calendarConnected && calendarName && (
        <Card className="border-green-200 bg-green-50">
          <CardContent className="py-3">
            <div className="flex items-center gap-2 text-green-700">
              <CheckCircle2 className="w-4 h-4" />
              <span className="text-sm">Connected to <strong>{calendarName}</strong></span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* View Tabs & Filters */}
      <Card>
        <CardContent className="py-4">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* View Tabs */}
            <div className="flex gap-2">
              <Button
                variant={activeView === "all" ? "default" : "outline"}
                size="sm"
                onClick={() => setActiveView("all")}
              >
                All ({totalAppointments + totalCalendarEvents})
              </Button>
              <Button
                variant={activeView === "calendar" ? "default" : "outline"}
                size="sm"
                onClick={() => setActiveView("calendar")}
                disabled={!calendarConnected}
              >
                <Calendar className="w-4 h-4 mr-1" />
                Calendar ({totalCalendarEvents})
              </Button>
              <Button
                variant={activeView === "booked" ? "default" : "outline"}
                size="sm"
                onClick={() => setActiveView("booked")}
              >
                <CalendarDays className="w-4 h-4 mr-1" />
                AI Booked ({totalAppointments})
              </Button>
            </div>

            {/* Search & Filter */}
            <div className="flex-1 flex gap-4">
              <div className="flex-1 relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  placeholder="Search appointments..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 border border-slate-300 rounded-lg text-sm"
              >
                <option value="all">All Status</option>
                <option value="scheduled">Scheduled</option>
                <option value="confirmed">Confirmed</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
              <Button variant="outline" size="sm" onClick={refreshAll} title="Refresh now (auto-syncs every 5 min)">
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
          </div>
          {lastSyncTime && (
            <div className="mt-2 text-xs text-slate-400 flex items-center gap-1">
              <RefreshCw className="w-3 h-3" />
              Last synced: {lastSyncTime.toLocaleTimeString()} • Auto-sync every 5 min
            </div>
          )}
        </CardContent>
      </Card>

      {/* Appointments List */}
      {Object.keys(filteredItems).length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <CalendarDays className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-700 mb-2">
              {!calendarConnected ? "Connect Your Calendar" : "No Appointments Found"}
            </h3>
            <p className="text-slate-500">
              {!calendarConnected 
                ? "Connect your Google Calendar to see your meetings here"
                : "No appointments match your current filters"
              }
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {Object.entries(filteredItems).map(([date, items]) => (
            <div key={date}>
              <h3 className="text-sm font-medium text-slate-500 mb-3 flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                {date}
              </h3>
              <div className="space-y-3">
                {items.map((item) => (
                  <Card key={item.id} className="hover:shadow-md transition-shadow">
                    <CardContent className="py-4">
                      <div className="flex flex-col lg:flex-row lg:items-start gap-4">
                        {/* Time */}
                        <div className="flex items-center gap-3 lg:w-28 flex-shrink-0">
                          <Clock className="w-5 h-5 text-indigo-600" />
                          <div>
                            <p className="font-medium text-slate-900">{formatTime(item.start_time)}</p>
                            {item.end_time && !item.is_all_day && (
                              <p className="text-xs text-slate-500">to {formatTime(item.end_time)}</p>
                            )}
                          </div>
                        </div>

                        {/* Main Content */}
                        <div className="flex-1 min-w-0">
                          {/* Title & Source */}
                          <div className="flex items-start gap-2 mb-2">
                            <h4 className="font-medium text-slate-900 truncate">
                              {item.title || item.customer_name || "Untitled"}
                            </h4>
                            {getSourceBadge(item.source)}
                          </div>

                          {/* Details based on source */}
                          {item.source === "google_calendar" ? (
                            <div className="space-y-1 text-sm text-slate-600">
                              {item.description && (
                                <p className="line-clamp-2">{item.description}</p>
                              )}
                              {item.location && (
                                <p className="flex items-center gap-1">
                                  <MapPin className="w-3 h-3" />
                                  {item.location}
                                </p>
                              )}
                              {item.attendees && item.attendees.length > 0 && (
                                <div className="flex items-center gap-2 flex-wrap">
                                  <Users className="w-3 h-3" />
                                  {item.attendees.slice(0, 3).map((a, idx) => (
                                    <span key={idx} className={`${getAttendeeStatusColor(a.status)}`}>
                                      {a.name || a.email}
                                      {idx < Math.min(item.attendees.length - 1, 2) && ","}
                                    </span>
                                  ))}
                                  {item.attendees.length > 3 && (
                                    <span className="text-slate-400">+{item.attendees.length - 3} more</span>
                                  )}
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="space-y-1 text-sm text-slate-600">
                              {item.customer_name && (
                                <p className="flex items-center gap-1">
                                  <User className="w-3 h-3" />
                                  {item.customer_name}
                                </p>
                              )}
                              {item.customer_phone && (
                                <p className="flex items-center gap-1">
                                  <Phone className="w-3 h-3" />
                                  {item.customer_phone}
                                </p>
                              )}
                              {item.customer_email && (
                                <p className="flex items-center gap-1">
                                  <Mail className="w-3 h-3" />
                                  {item.customer_email}
                                </p>
                              )}
                              {item.reason && (
                                <p className="flex items-center gap-1">
                                  <MessageSquare className="w-3 h-3" />
                                  {item.reason}
                                </p>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Status & Actions */}
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {item.source !== "google_calendar" && item.status && (
                            <>
                              {getStatusBadge(item.status)}
                              {item.synced_to_calendar && (
                                <Badge variant="outline" className="bg-blue-50 text-blue-600 border-blue-200">
                                  <Calendar className="w-3 h-3 mr-1" />
                                  Synced
                                </Badge>
                              )}
                            </>
                          )}
                          
                          {item.source === "google_calendar" && item.html_link && (
                            <a
                              href={item.html_link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-slate-400 hover:text-indigo-600"
                            >
                              <ExternalLink className="w-4 h-4" />
                            </a>
                          )}

                          {item.source !== "google_calendar" && (
                            <>
                              {item.status === "scheduled" && (
                                <>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => updateAppointmentStatus(item.id, "confirmed")}
                                    className="text-green-600 hover:text-green-700"
                                  >
                                    <CheckCircle2 className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => updateAppointmentStatus(item.id, "cancelled")}
                                    className="text-red-600 hover:text-red-700"
                                  >
                                    <XCircle className="w-4 h-4" />
                                  </Button>
                                </>
                              )}
                              {item.status === "confirmed" && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => updateAppointmentStatus(item.id, "completed")}
                                >
                                  Complete
                                </Button>
                              )}
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => deleteAppointment(item.id)}
                                className="text-slate-400 hover:text-red-600"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Stats */}
      {(totalAppointments > 0 || totalCalendarEvents > 0) && (
        <Card>
          <CardContent className="py-4">
            <div className="flex flex-wrap gap-6 text-sm">
              <div>
                <span className="text-slate-500">Total Events:</span>
                <span className="ml-2 font-medium">{totalAppointments + totalCalendarEvents}</span>
              </div>
              {calendarConnected && (
                <div>
                  <span className="text-slate-500">From Calendar:</span>
                  <span className="ml-2 font-medium text-blue-600">{totalCalendarEvents}</span>
                </div>
              )}
              <div>
                <span className="text-slate-500">AI Booked:</span>
                <span className="ml-2 font-medium text-indigo-600">{totalAppointments}</span>
              </div>
              {totalAppointments > 0 && (
                <>
                  <div>
                    <span className="text-slate-500">Scheduled:</span>
                    <span className="ml-2 font-medium text-blue-600">
                      {appointments.filter(a => a.status === "scheduled").length}
                    </span>
                  </div>
                  <div>
                    <span className="text-slate-500">Confirmed:</span>
                    <span className="ml-2 font-medium text-green-600">
                      {appointments.filter(a => a.status === "confirmed").length}
                    </span>
                  </div>
                </>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
